import * as Yup from 'yup';
import { AppEnum, MediaType } from '../Types';

export const isStringANumber = (value: string): boolean => {
	const num = +value;
	return !isNaN(num);
}

const phoneRegex = /^[0-9\s\-+().]*$/;
const noSpecialCharsRegex = /^[a-zA-Z0-9\s\-_'àâäéèêëïîôöùûüç.,;:'"?!()\-]*$/;
const noHTMLTagsRegex = /^(?![\s\S]*<[^>]+>)[\s\S]*$/u;

const passwordRegex = /^[\w\d!@#$%^&*()_+\-=\[\]{};:"\\|,.\/?]+$/;

export const validatePassword = ({ minLength = 4, maxLength = 12 }) =>
	Yup.string()
		.max(maxLength, `Ne doit pas dépasser ${maxLength} caractères`)
		.min(minLength, `Ne doit pas être moins de ${minLength} caractères`)
		.matches(passwordRegex, 'Contient des caractères invalides. Lettres, chiffres et certains caractères spéciaux (!@#$%^&*()_+-=[]{};:"|,./?) sont autorisés.');

export const stringLength = ({ minLength = 0, maxLength = 100 }) => Yup.string()
	.max(maxLength, `Ne doit pas dépasser ${maxLength} caractères`)
	.min(minLength, `Ne doit pas être moins de ${minLength} caractères`);

export const stringWithNoSpecialChars = ({ minLength = 0, maxLength = 100 }) =>
	stringLength({ minLength, maxLength })
		.matches(noSpecialCharsRegex, 'Contient des caractères invalides');

export const stringWithNoHTMLTags = ({ minLength = 0, maxLength = 100 }) =>
	stringLength({ minLength, maxLength })
		.matches(noHTMLTagsRegex, 'Contient des balise invalides (Balise HTML)');

export const validateCivility = (CivilityEnum: AppEnum[]) =>
	Yup.string()
		.oneOf(CivilityEnum.map(opt => opt.value as string), 'Civilité invalide')
		.required('La civilité est requise');

export const validateLeaseType = (LeaseTypeEnum: AppEnum[]) =>
	Yup.string()
		.oneOf(LeaseTypeEnum.map(opt => opt.value as string), 'Type de bail invalide.')
		.required('Le type de bail est requis.');

export const validateOptionalLeaseOptions = (LeaseOptionEnum: AppEnum[]) =>
	Yup.array()
		.of(
			Yup.string()
				.oneOf(
					LeaseOptionEnum.map(opt => opt.value as string),
					'Option de bail invalide.'
				)
		)
		.nullable();


export const validateUserRole = (UserRoleEnum: AppEnum[]) =>
	Yup.string()
		.oneOf(UserRoleEnum.map(opt => opt.value as string), 'Rôle invalide')
		.required("Le rôle de l'agent est requis.");

export const validateContactType = (ContactTypeEnum: AppEnum[]) => Yup.string()
	.oneOf(ContactTypeEnum.map(opt => opt.value as string), 'Type de contact invalide')
	.required('Le type de contact est requis.');

export const validateAmountRangeType = (ProceRangeEnum: AppEnum[]) => Yup.string()
	.oneOf(ProceRangeEnum.map(opt => opt.value as string), 'La fourchette de prix est invalide')
	.required('La fourchette de prix est requise.');

export const validateFloat = () =>
	Yup.string().test(
		'is-valid-float',
		'Doit être un nombre valide',
		(value) => {
			if (!value || typeof value !== 'string') return true; // Tolère vide

			const normalized = value.replace(',', '.').trim();

			// Regex stricte : accepte entiers ou décimaux positifs/négatifs
			const isValidFormat = /^-?\d+(\.\d+)?$/.test(normalized);

			if (!isValidFormat) return false;

			const parsed = parseFloat(normalized);
			return !isNaN(parsed);
		}
	);


	export const validateAmount = (errorMessage = 'Doit être un montant valide (entier positif)') =>
	Yup.string().test(
		'is-valid-price',
		errorMessage,
		(value) => {
			if (!value || typeof value !== 'string') return true; // Tolère vide

			const normalized = value.replace(',', '.').replace(/\s/g, '').trim();

			// Regex stricte : accepte entiers ou décimaux positifs/négatifs
			const isValidFormat = /^-?\d+(\.\d+)?$/.test(normalized);

			if (!isValidFormat) return false;

			const parsed = parseFloat(normalized);
			return !isNaN(parsed);
		}
	);


export const validateNumericId = () =>
	Yup.string()
		.matches(/^\d+$/, 'Doit être un identifiant numérique (entier uniquement)');

export const validateNumber = () =>
	Yup.number().transform((value, originalValue) => {
		return originalValue === '' ? undefined : Number(originalValue);
	})
		.typeError('Doit être un identifiant numérique')
		.integer('Doit être un entier')
//.positive('Doit être positif');

export const validateOptionalNumber = () => validateNumber().nullable().notRequired();

export const validateEmail = () => Yup.string().email('Adresse email invalide');

export const validatePhone = () =>
	Yup.string()
		.max(20, 'Le numéro de téléphone ne doit pas dépasser 20 caractères')
		.matches(phoneRegex, 'Numéro de téléphone invalide');


export const validateDate = () =>
	Yup.string()
		.test('is-date', 'La date est invalide', value => !value || !isNaN(Date.parse(value)))


export const validateAvailability = (availabilityEnum: AppEnum[]) => Yup.string()
	.oneOf(availabilityEnum.map(opt => opt.value as string), 'Type de disponibilité invalide')
	.required('La disponibilité est requise.');

export const validatePropertyType = (typeEnum: AppEnum[]) => Yup.string()
	.oneOf(typeEnum.map(opt => opt.value as string), 'Le type de bien est invalide')
	.notRequired();//required('Le type de bien est requis.');

export const validateOperationType = (operationTypeEnum: AppEnum[]) => Yup.string()
	.oneOf(operationTypeEnum.map(opt => opt.value as string), "Le type d'opération est invalide")
	.required("Le type d'opération est requise.");


export const validatePropertyUsageType = (propertyUsageEnum: AppEnum[], optional = false) => {
	
	const checkOption = Yup.string().oneOf(propertyUsageEnum.map(opt => opt.value as string), "Le type d'usage est invalide");

	return optional 
	? checkOption 
	: checkOption.required("Le type d'opération est requise.");
}

	

export const validateTargetType = (targetTypeEnum: AppEnum[]) => Yup.string()
	.oneOf(targetTypeEnum.map(opt => opt.value as string), "Le type de mandat est invalid")
	.required("Le type de mandat est requis.");

export const validateActivityPriority = (priorityEnum: AppEnum[]) => Yup.string()
	.oneOf(priorityEnum.map(opt => opt.value as string), "La priorité de l'activité est invalide")
	.required("La priorité de l'activité est requise.");

export const validateActivityStatus = (statusEnum: AppEnum[]) => Yup.string()
	.oneOf(statusEnum.map(opt => opt.value as string), "Le statut de l'activité est invalide")
	.required("Le statut de l'activité est requis.");

export const validatePropertyCategory = (categoryEnum: AppEnum[]) => Yup.string()
	.oneOf(categoryEnum.map(opt => opt.value as string), 'La catégorie de bien est invalide')
	.required('La catégorie est requise.');

export const validateTaskCategory = (categoryEnum: AppEnum[]) => Yup.string()
	.oneOf(categoryEnum.map(opt => opt.value as string), 'La catégorie de la tâche est invalide')
	.required('La catégorie de la tâche est requise.');

export const validateNeedStatus = (needStatusEnum: AppEnum[]) => Yup.string()
	.oneOf(needStatusEnum.map(opt => opt.value as string), 'Le status du besoin est invalide')
	.required('Le statut du besoin requis.');

export const validatePlanningType = (planningTypeEnum: AppEnum[]) => Yup.string()
	.oneOf(planningTypeEnum.map(opt => opt.value as string), 'Le type de planning est invalide')
	.required('Le type est requis.');

export const validateMatchingStatus = (matchingStatusEnum: AppEnum[]) => Yup.string()
	.oneOf(matchingStatusEnum.map(opt => opt.value as string), 'Le status du rapprochement est invalide')
	.required('Le statut du rapprochement requis.');


export const validatePropertyCondition = (conditionEnum: AppEnum[]) => Yup.string()
	.oneOf(conditionEnum.map(opt => opt.value as string), 'L\'état général du bien est invalide')
	.required('L\'état général du bien est requis.');

export const validatePropertyStanding = (standingEnum: AppEnum[]) => Yup.string()
	.oneOf(standingEnum.map(opt => opt.value as string), 'Le standing du bien est invalide')
	.required('Le standing du bien est requis.');

export const validateMediaType = (type: MediaType) => Yup.string().test(value => type === value)
	.required('Le type de média est requis.');

export const validateDocumentType = (typeEnum: AppEnum[]) => Yup.string()
	.oneOf(typeEnum.map(opt => opt.value as string), 'Le type de bien est invalide')
	.required('Le type de document est requis.');

export const validateDocumentCategory = (typeEnum: AppEnum[]) => Yup.string()
	.oneOf(typeEnum.map(opt => opt.value as string), 'La categorie du document est invalide')
	.required('La catégorie du document est requiss.');


export const validateFileByExtention = (fileOptions: { maxSizeInMB: number; allowedExtentions: string[] }) =>
	Yup.mixed<File>()
		.test(
			'fileExtention',
			'Type de fichier non supporté',
			file => {
				const fileExtention = file?.name.split('.').pop();
				return !file || (!!fileExtention && fileOptions.allowedExtentions.includes('.' + fileExtention));
			}
		)
		.test(
			'fileSize',
			`Le fichier dépasse ${fileOptions.maxSizeInMB} Mo`,
			file => !file || file.size <= fileOptions.maxSizeInMB * 1024 * 1024
		);

export const validateFileByType = (fileOptions: { maxSizeInMB: number; allowedTypes: string[] }) =>
	Yup.mixed<File>()
		.test(
			'fileType',
			'Type de fichier non supporté',
			file => !file || fileOptions.allowedTypes.includes(file.type)
		)
		.test(
			'fileSize',
			`Le fichier dépasse ${fileOptions.maxSizeInMB} Mo`,
			file => !file || file.size <= fileOptions.maxSizeInMB * 1024 * 1024
		);

export const validateOptionalUrl = (fieldName = 'URL') =>
	Yup.string()
		.nullable()
		.notRequired()
		.test('is-url', `${fieldName} invalide`, (value) => {
			if (!value) return true; // Laisser passer les champs vides
			try {
				new URL(value);
				return true;
			} catch {
				return false;
			}
		});

export const validateStartTime = () => {
	return Yup.string()
		.test('is-before-end', 'L\'heure de début doit être avant l\'heure de fin', function (value) {
			const { endTime } = this.parent;
			return !endTime || !value || timeToMinutes(value) <= timeToMinutes(endTime);
		});
}

export const validateEndTime = () => {
	return Yup.string()
		.test('is-after-start', 'L\'heure de fin doit être après l\'heure de début', function (value) {
			const { startTime } = this.parent;
			return !startTime || !value || timeToMinutes(value) >= timeToMinutes(startTime);
		});
}


function timeToMinutes(time: string) {
	const [h, m] = time.split(':').map(Number);
	return h * 60 + m;
}


export const validateAccessType = (accessTypeEnum: AppEnum[]) => {

	const validValues = accessTypeEnum.map(a => a.value as string);

	return Yup.array()
		.of(Yup.string().oneOf(validValues, "Droit d'accès invalide."))
		.min(1, "Vous devez sélectionner au moins un droit d'accès.")
		.required("Les droits d'accès sont obligatoires.");
};